<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import EnginePanel from './components/EnginePanel.vue';
import DefinitionTransfer from './components/DefinitionTransfer.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const flows = computed(() => store.getters['teletelFlows/getFlows']);
const meta = computed(() => store.getters['teletelFlows/getMeta']);
const uiFlags = computed(() => store.getters['teletelFlows/getUIFlags']);

onMounted(() => store.dispatch('teletelFlows/get', { archived: true }));

const goTo = name => router.push({ name });
// The minimal flow report: last-30-day completion and where customers stop.
const completionLabel = flow => {
  const stats = flow.stats || {};
  if (!stats.runs_30d) return '—';
  return `${stats.finished_30d} / ${stats.runs_30d} (${stats.completion_pct}%)`;
};
const topStopLabel = flow => {
  const node = flow.stats?.top_stop_node;
  return node ? `${node.name} ×${node.count}` : '—';
};
const editFlow = flow =>
  router.push({ name: 'teletel_flows_edit', params: { flowId: flow.id } });

const publishFlow = async flow => {
  try {
    await store.dispatch('teletelFlows/publish', flow.id);
    useAlert(t('TELETEL_FLOWS.ACTIONS.PUBLISH_DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
const deactivateFlow = flow =>
  store.dispatch('teletelFlows/deactivate', flow.id);
// A flow with runs is archived instead of deleted (owner order 2026-09-16):
// off the list, never triggered, history kept; restore brings it back.
const showArchived = ref(false);
const visibleFlows = computed(() =>
  showArchived.value ? flows.value : flows.value.filter(flow => !flow.archived)
);
const hasArchived = computed(() => flows.value.some(flow => flow.archived));
const archiveFlow = async flow => {
  if (!window.confirm(t('TELETEL_FLOWS.ACTIONS.CONFIRM_ARCHIVE'))) return;
  try {
    await store.dispatch('teletelFlows/archive', flow.id);
    useAlert(t('TELETEL_FLOWS.ACTIONS.ARCHIVE_DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
const restoreFlow = async flow => {
  try {
    await store.dispatch('teletelFlows/restore', flow.id);
    useAlert(t('TELETEL_FLOWS.ACTIONS.RESTORE_DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
// Archived flows only: unlink the run history and delete everything
// (owner order 2026-09-16). The server refuses when lifecycle enrollments
// belong to the flow; that message is shown as-is.
const purgeFlow = async flow => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('TELETEL_FLOWS.ACTIONS.CONFIRM_PURGE', { count: flow.runs_count })
    )
  )
    return;
  try {
    await store.dispatch('teletelFlows/purge', flow.id);
    useAlert(t('TELETEL_FLOWS.ACTIONS.PURGE_DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
const deleteFlow = async flow => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_FLOWS.ACTIONS.CONFIRM_DELETE'))) return;
  try {
    await store.dispatch('teletelFlows/delete', flow.id);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_FLOWS.HEADER')"
      :description="t('TELETEL_FLOWS.DESCRIPTION')"
    >
      <template #actions>
        <NextButton slate sm @click="goTo('teletel_flows_runs')">
          {{ t('TELETEL_FLOWS.LIST.RUNS_PAGE') }}
        </NextButton>
        <NextButton slate sm @click="goTo('teletel_flows_templates')">
          {{ t('TELETEL_FLOWS.TEMPLATES.LINK') }}
        </NextButton>
        <NextButton sm @click="goTo('teletel_flows_new')">
          {{ t('TELETEL_FLOWS.LIST.NEW_FLOW') }}
        </NextButton>
      </template>
    </PageHeader>

    <div
      v-if="meta.selectorErrors > 0"
      class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6"
    >
      {{
        t('TELETEL_FLOWS.SELECTOR_ALERT', {
          count: meta.selectorErrors,
          last: meta.selectorLastError || '—',
        })
      }}
    </div>

    <p v-if="!uiFlags.isFetching && !flows.length" class="text-n-slate-11">
      {{ t('TELETEL_FLOWS.LIST.404') }}
    </p>

    <label
      v-if="hasArchived"
      class="mb-2 flex w-fit cursor-pointer items-center gap-2 text-xs text-n-slate-11"
    >
      <input v-model="showArchived" type="checkbox" class="!mb-0" />
      {{ t('TELETEL_FLOWS.ACTIONS.SHOW_ARCHIVED') }}
    </label>
    <table v-if="flows.length" class="w-full text-sm divide-y divide-n-weak">
      <thead>
        <tr class="text-start text-n-slate-11">
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.NAME') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.TRIGGER') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.STATUS') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.NODES') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.RUNS') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.COMPLETION') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.TOP_STOP') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <tr v-for="flow in visibleFlows" :key="flow.id">
          <td class="py-2 font-medium text-n-slate-12">{{ flow.name }}</td>
          <td class="py-2">
            {{ t(`TELETEL_FLOWS.TRIGGERS.${flow.trigger_type}`) }}
          </td>
          <td class="py-2">
            <span
              class="px-2 py-0.5 rounded-full text-xs"
              :class="
                flow.status === 'active'
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{
                t(
                  `TELETEL_FLOWS.STATUS.${flow.archived ? 'archived' : flow.status}`
                )
              }}
            </span>
          </td>
          <td class="py-2">{{ flow.node_count }}</td>
          <td class="py-2">{{ flow.runs_count }}</td>
          <td class="py-2 tabular-nums">{{ completionLabel(flow) }}</td>
          <td class="py-2">{{ topStopLabel(flow) }}</td>
          <td class="py-2">
            <div class="flex gap-1">
              <NextButton slate xs @click="editFlow(flow)">
                {{ t('TELETEL_FLOWS.ACTIONS.EDIT') }}
              </NextButton>
              <NextButton
                v-if="flow.status === 'draft' && !flow.archived"
                teal
                xs
                @click="publishFlow(flow)"
              >
                {{ t('TELETEL_FLOWS.ACTIONS.PUBLISH') }}
              </NextButton>
              <NextButton
                v-else-if="flow.status === 'active'"
                amber
                xs
                @click="deactivateFlow(flow)"
              >
                {{ t('TELETEL_FLOWS.ACTIONS.DEACTIVATE') }}
              </NextButton>
              <template v-if="flow.archived">
                <span
                  class="inline-flex items-center rounded-md bg-n-amber-3 px-2 py-0.5 text-xs text-n-amber-11"
                >
                  {{
                    t('TELETEL_FLOWS.ACTIONS.LINKED_RUNS', {
                      count: flow.runs_count,
                    })
                  }}
                </span>
                <NextButton slate xs @click="restoreFlow(flow)">
                  {{ t('TELETEL_FLOWS.ACTIONS.RESTORE') }}
                </NextButton>
                <NextButton
                  v-tooltip="t('TELETEL_FLOWS.ACTIONS.PURGE_HINT')"
                  ruby
                  xs
                  @click="purgeFlow(flow)"
                >
                  {{ t('TELETEL_FLOWS.ACTIONS.PURGE') }}
                </NextButton>
              </template>
              <NextButton
                v-else-if="flow.runs_count > 0"
                v-tooltip="t('TELETEL_FLOWS.ACTIONS.ARCHIVE_HINT')"
                ruby
                xs
                @click="archiveFlow(flow)"
              >
                {{ t('TELETEL_FLOWS.ACTIONS.ARCHIVE') }}
              </NextButton>
              <NextButton v-else ruby xs @click="deleteFlow(flow)">
                {{ t('TELETEL_FLOWS.ACTIONS.DELETE') }}
              </NextButton>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <DefinitionTransfer @imported="store.dispatch('teletelFlows/get')" />
    <EnginePanel />
  </div>
</template>
